import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useLocation, Link } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function SupportTicket() {
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, logout } = useAuth();
  const token = searchParams.get("token");
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    const params = token ? { token } : {};
    api.get(`/support/tickets/${ticketId}`, { params })
      .then(({ data }) => setTicket(data))
      .catch((e) => {
        if (e.response?.status === 403) {
          setAccessDenied(true);
        } else {
          toast.error(formatApiError(e));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [ticketId, token]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const params = token ? { token } : {};
      const { data } = await api.post(`/support/tickets/${ticketId}/reply`, { message: reply }, { params });
      setTicket(data);
      setReply("");
      toast.success("Reply sent");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <PublicLayout><div className="max-w-3xl mx-auto py-20 text-center text-muted-foreground">Loading…</div></PublicLayout>;
  if (accessDenied) {
    const from = location.pathname + location.search;
    if (user) {
      return (
        <PublicLayout>
          <div className="max-w-3xl mx-auto py-20 text-center">
            <div className="mb-4">This conversation is linked to a different account.<br />Please sign out and log in with the account that received this message.</div>
            <Button onClick={() => logout().then(() => window.location.reload())}>Log out</Button>
          </div>
        </PublicLayout>
      );
    }
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto py-20 text-center">
          <div className="mb-4">Please log in to view this conversation.</div>
          <Button asChild><Link to="/login" state={{ from }}>Log in</Link></Button>
        </div>
      </PublicLayout>
    );
  }
  if (!ticket) return <PublicLayout><div className="max-w-3xl mx-auto py-20 text-center">Message not found.</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-[10px] uppercase tracking-[0.2em] text-accent font-bold mb-3">Support</div>
        <div className="flex items-center justify-between gap-3 mb-1">
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{ticket.subject}</h1>
          <Badge variant={ticket.status === "open" ? "default" : "outline"} className="capitalize shrink-0">{ticket.status}</Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-8">Started {new Date(ticket.created_at).toLocaleString()}</p>

        <div className="space-y-4 mb-8">
          {ticket.messages.map((m, i) => (
            <Card key={i} className={`border-border p-4 ${m.sender === "admin" ? "bg-primary/5" : ""}`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex justify-between">
                <span>{m.sender === "admin" ? "RydeXpress Support" : ticket.name}</span>
                <span>{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{m.body}</p>
            </Card>
          ))}
        </div>

        <Card className="border-border p-4">
          <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add a reply…" data-testid="ticket-reply-input" />
          <Button onClick={sendReply} disabled={sending || !reply.trim()} className="mt-3 bg-primary hover:bg-primary/90" data-testid="ticket-reply-btn">
            {sending ? "Sending…" : "Send reply"}
          </Button>
        </Card>
      </section>
    </PublicLayout>
  );
}
